import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { Alert } from "../../../components/ui/Alert";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { Card } from "../../../components/ui/Card";
import { CheckboxField } from "../../../components/ui/CheckboxField";
import { FormField } from "../../../components/ui/FormField";
import { AppShell } from "../../../layouts/AppShell";
import { ApiError } from "../../../lib/api/client";
import {
  createMaterialWithFile,
  listMaterials,
} from "../api/materialApi";
import { MaterialDeleteControl } from "../components/MaterialDeleteControl";
import { MaterialWorkflow } from "../components/MaterialWorkflow";
import type { Material } from "../schemas/materialSchemas";

const reviewLabel = { DRAFT: "待審查", REVIEWED: "已審查" } as const;
const publicationLabel = {
  DRAFT: "草稿",
  PUBLISHED: "已發布",
  WITHDRAWN: "已撤下",
} as const;
const publicationVariant = {
  DRAFT: "secondary",
  PUBLISHED: "success",
  WITHDRAWN: "danger",
} as const;

const MAX_MATERIAL_FILE_BYTES = 50 * 1024 * 1024;
const FILE_TOO_LARGE_MESSAGE = "檔案大小不可超過 50 MiB。";

export function MaterialManagePage() {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ["materials", "admin"],
    queryFn: () => listMaterials(),
  });
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File>();
  const [rights, setRights] = useState(false);
  const [notice, setNotice] = useState<string>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!file) {
      setError("請選擇教材檔案。");
      return;
    }
    if (file.size > MAX_MATERIAL_FILE_BYTES) {
      setError(FILE_TOO_LARGE_MESSAGE);
      return;
    }
    setLoading(true);
    setNotice(undefined);
    setError(undefined);
    try {
      await createMaterialWithFile({ title, description }, file, rights);
      setNotice("教材草稿與檔案已建立，檔案掃描完成。");
      setTitle("");
      setDescription("");
      setFile(undefined);
      setRights(false);
      await client.invalidateQueries({ queryKey: ["materials"] });
    } catch (caught) {
      const message =
        caught instanceof ApiError && caught.problem.status === 413
          ? FILE_TOO_LARGE_MESSAGE
          : caught instanceof Error
            ? caught.message
            : "建立教材失敗。";
      setError(`教材建立失敗：${message}`);
    } finally {
      setLoading(false);
    }
  };
  const updateMaterial = (material: Material) => {
    client.setQueryData(["material", material.id], material);
    void client.invalidateQueries({ queryKey: ["materials"] });
  };

  return (
    <AppShell title="教材管理" breadcrumb={["管理後台", "教材管理"]}>
      {notice && <Alert variant="success">{notice}</Alert>}
      {error && <Alert variant="danger">{error}</Alert>}
      <Card title="新增教材">
        <form onSubmit={(event) => void submit(event)}>
          <FormField
            label="教材名稱"
            required
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
          <FormField
            label="說明"
            required
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
          <FormField
            label="教材檔案"
            type="file"
            required
            help="單一檔案上限為 50 MiB。"
            onChange={(event) => {
              const selected = event.target.files?.[0];
              setFile(selected);
              setError(
                selected && selected.size > MAX_MATERIAL_FILE_BYTES
                  ? FILE_TOO_LARGE_MESSAGE
                  : undefined,
              );
            }}
          />
          <CheckboxField
            label="我確認擁有發布此教材的權利"
            required
            checked={rights}
            onChange={(event) => setRights(event.target.checked)}
          />
          <Button type="submit" loading={loading}>
            建立並上傳
          </Button>
        </form>
      </Card>
      {query.data?.items.map((material) => (
        <Card key={material.id} title={material.title}>
          <div className="row">
            <div className="col-lg-8">
              <div className="font-weight-bold text-gray-800 mb-1">
                教材說明
              </div>
              <p className="text-gray-700 mb-3">
                {material.description || "未填寫說明"}
              </p>
              {material.currentVersion && (
                <div className="small text-muted">
                  檔案：
                  <span className="text-gray-700">
                    {material.currentVersion.filename}
                  </span>
                  <span className="mx-2">·</span>版本{" "}
                  {material.currentVersion.versionNo}
                </div>
              )}
            </div>
            <div className="col-lg-4 mt-3 mt-lg-0">
              <div className="rounded bg-light p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="small text-muted">審查狀態</span>
                  <Badge
                    variant={
                      material.reviewStatus === "REVIEWED"
                        ? "primary"
                        : "warning"
                    }
                  >
                    {reviewLabel[material.reviewStatus]}
                  </Badge>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="small text-muted">發布狀態</span>
                  <Badge
                    variant={publicationVariant[material.publicationStatus]}
                  >
                    {publicationLabel[material.publicationStatus]}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
          <MaterialWorkflow material={material} onChanged={updateMaterial} />
          <MaterialDeleteControl
            material={material}
            onDeleted={() => {
              setNotice("教材已刪除。");
              void client.invalidateQueries({ queryKey: ["materials"] });
            }}
          />
        </Card>
      ))}
    </AppShell>
  );
}
